import { Controller } from '@hotwired/stimulus'
import Sortable from "sortablejs"
import { renderStreamMessage } from '@hotwired/turbo'
import { patch, post } from '@rails/request.js'
import { showFlashMessage } from '../../helpers'
import { elementsByDataSlot, elementsForAdjacentWeeks, refreshTurboFrames } from '../../helpers/calendar/turbo_frames'

export default class extends Controller {
  static targets = ['slot', 'updateForm', 'addForm', 'addActivityForm',
                    'lessonsPalette', 'activityPalette', 'scrollRoot']

  static get SORTABLE_CONFIG() {
    return {
      animation: 150,
      scrollSensitivity: 800,
      swapThreshold: 0.9,
      ghostClass: 'calendar-ghost',
      forceFallback: false
    }
  }

  static get SLOT_CONFIG() {
    return {
      ...this.SORTABLE_CONFIG,
      group: {
        name: 'calendar',
        put: (to, _from, dragged) => {
          const draggables = Array.from(to.el.children).filter(child => !child.classList.contains('undraggable'))

          // Max 2 calendar entries per slot
          if (draggables.length >= 2) return false

          // Do not drag any calendar entry on top of a two-week calendar entry
          if (draggables.some(child => child.dataset.calendarEntryTwoWeeks === 'true')) return false

          // Do not drag a two-week calendar entry on top of any other calendar entry
          if (dragged.dataset.calendarEntryTwoWeeks === 'true' && draggables.length > 0) return false

          return true
        }
      },
      filter: '.undraggable'
    }
  }

  static get PALETTE_CONFIG() {
    return {
      ...this.SORTABLE_CONFIG,
      group: { name: 'calendar', pull: 'clone', put: false },
      sort: false
    }
  }

  connect() {
    if (this.slotTargets.length > 0) {
      this.createSlotObserver()
      this.slotTargets.forEach(slot => this.slotObserver.observe(slot))
    }

    if (this.hasLessonsPaletteTarget) {
      this.initializePalette(this.lessonsPaletteTarget, '.sortable-lesson', this.handleAddLesson.bind(this))
    }

    if (this.hasActivityPaletteTarget) {
      this.initializePalette(this.activityPaletteTarget, '.sortable-activity', this.handleAddActivity.bind(this))
    }
  }

  disconnect() {
    if (this.slotObserver) {
      this.slotObserver.disconnect()
      this.slotObserver = null
    }
  }

  slotTargetConnected(element) {
    if (this.slotObserver) {
      this.slotObserver.observe(element)
      return
    }
    if (!element.sortable) {
      this.initializeSingleSlot(element)
    }
  }

  slotTargetDisconnected(element) {
    if (this.slotObserver) {
      this.slotObserver.unobserve(element)
    }
    if (element.sortable) {
      element.sortable.destroy()
      element.sortable = null
    }
  }

  initializeSingleSlot(slot) {
    if (slot.sortable) {
      return
    }

    slot.sortable = new Sortable(slot, {
      ...this.constructor.SLOT_CONFIG,
      onStart: () => {
        document.body.classList.add('is-dragging')
      },
      onEnd: async (evt) => {
        document.body.classList.remove('is-dragging')
        await this.handleMoveEvent(evt)
      }
    })
  }

  createSlotObserver() {
    if (this.slotObserver) return

    const root = this.hasScrollRootTarget ? this.scrollRootTarget : null
    this.slotObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const slot = entry.target
        if (entry.isIntersecting) {
          if (!slot.sortable) {
            this.initializeSingleSlot(slot)
          }
        } else {
          if (slot.sortable) {
            slot.sortable.destroy()
            slot.sortable = null
          }
        }
      })
    }, { root, rootMargin: '200px' })
  }

  scrollRootTargetConnected(_element) {
    this.recreateSlotObserver()
  }

  scrollRootTargetDisconnected(_element) {
    this.destroySlotObserver()
  }

  destroySlotObserver() {
    if (this.slotObserver) {
      this.slotObserver.disconnect()
      this.slotObserver = null
    }
  }

  recreateSlotObserver() {
    this.destroySlotObserver()
    this.createSlotObserver()
    if (this.slotObserver) {
      this.slotTargets.forEach(slot => this.slotObserver.observe(slot))
    }
  }

  initializePalette(target, draggableSelector, onEndHandler) {
    new Sortable(target, {
      ...this.constructor.PALETTE_CONFIG,
      draggable: draggableSelector,
      onEnd: onEndHandler
    })
  }

  async handleMoveEvent(evt) {
    const item = evt.item
    const position = evt.newIndex
    const targetSlot = evt.to

    const previousState = {
      originalParent: evt.from,
      originalIndex: evt.oldIndex,
      item: item
    }

    const slotData = {
      week_start_date: targetSlot.getAttribute('data-week'),
      sequence_id: targetSlot.getAttribute('data-sequence'),
      calendar_entry_id: item.getAttribute('data-calendar-entry-id'),
      position: position
    }

    this.populateForm(this.updateFormTarget, slotData)

    try {
      const response = await patch(this.updateFormTarget.action, {
        body: new FormData(this.updateFormTarget),
        responseKind: 'turbo-stream'
      })

      if (response.ok) {
        // The server's turbo_stream replace has already swapped the moved
        // card's HTML in place, so its lazy action menu frame is fresh and
        // does not need a refresh — only OTHER cards (other sequences showing
        // the same lesson, or adjacent weeks whose extend/shrink availability
        // depends on the move) need their frames refreshed.
        const movedCard = targetSlot.querySelector(`[data-schedulable-id="${item.dataset.schedulableId}"]`)
        const sourceSlot = previousState.originalParent

        const containers = [
          ...elementsByDataSlot('schedulable-id', item.dataset.schedulableId),
          ...elementsForAdjacentWeeks(sourceSlot.dataset.sequence, sourceSlot.dataset.term, parseInt(sourceSlot.dataset.termWeek)),
          ...elementsForAdjacentWeeks(targetSlot.dataset.sequence, targetSlot.dataset.term, parseInt(targetSlot.dataset.termWeek))
        ]
        const framesToRefresh = new Set()
        containers.forEach(element => {
          if (element.tagName === 'TURBO-FRAME') {
            framesToRefresh.add(element)
          } else {
            element.querySelectorAll('turbo-frame').forEach(f => framesToRefresh.add(f))
          }
        })
        if (movedCard) {
          movedCard.querySelectorAll('turbo-frame').forEach(f => framesToRefresh.delete(f))
        }
        framesToRefresh.forEach(frame => { frame.src = frame.src })
      } else {
        // The server has already replaced #flashes with the validation error
        // via turbo_stream, so we only need to roll back the optimistic move.
        this.rollbackMove(previousState)
      }
    } catch (error) {
      showFlashMessage('Network error. Please try again.')
      this.rollbackMove(previousState)
    }
  }

  async handleAddLesson(evt) {
    if (!this.isValidDrop(evt)) return

    const { item, targetSlot, position } = this.extractEventData(evt)
    const previousState = { item, targetSlot }

    // Keep the placeholer style until server responds
    item.classList.add('calendar-ghost')

    const slotData = {
      week_start_date: targetSlot.getAttribute('data-week'),
      sequence_id: targetSlot.getAttribute('data-sequence'),
      lesson_id: item.getAttribute('data-lesson-id'),
      position: position
    }

    this.populateForm(this.addFormTarget, slotData)

    await this.submitAddForm(this.addFormTarget, previousState, 'Failed to add lesson. Please try again.', () => {
      const targetTermId = targetSlot.dataset.term
      const targetWeekNumber = parseInt(targetSlot.dataset.termWeek)
      refreshTurboFrames(elementsForAdjacentWeeks(targetSlot.dataset.sequence, targetTermId, targetWeekNumber))
    })
  }

  async handleAddActivity(evt) {
    if (!this.isValidDrop(evt)) return

    const { item, targetSlot, position } = this.extractEventData(evt)
    const previousState = { item, targetSlot }

    // Keep the placeholer style until server responds
    item.classList.add('calendar-ghost')

    const slotData = {
      week_start_date: targetSlot.getAttribute('data-week'),
      sequence_id: targetSlot.getAttribute('data-sequence'),
      activity_kind: item.getAttribute('data-activity-kind'),
      position: position
    }

    this.populateForm(this.addActivityFormTarget, slotData)

    await this.submitAddForm(this.addActivityFormTarget, previousState, 'Failed to add activity. Please try again.', () => {
      const targetTermId = targetSlot.dataset.term
      const targetWeekNumber = parseInt(targetSlot.dataset.termWeek)
      refreshTurboFrames(elementsForAdjacentWeeks(targetSlot.dataset.sequence, targetTermId, targetWeekNumber))
    })
  }

  populateForm(form, data) {
    Object.entries(data).forEach(([fieldName, value]) => {
      const field = form.querySelector(`[name="${fieldName}"]`)
      if (field && value !== undefined) {
        field.value = value
      }
    })
  }

  rollbackMove(previousState) {
    const { originalParent, originalIndex, item } = previousState

    if (originalIndex >= originalParent.children.length) {
      originalParent.appendChild(item)
    } else {
      originalParent.insertBefore(item, originalParent.children[originalIndex])
    }
  }

  rollbackAdd(previousState) {
    const { item, targetSlot } = previousState

    if (item.parentNode === targetSlot) {
      targetSlot.removeChild(item)
    }
  }

  async handleErrorResponse(response, defaultMessage) {
    try {
      const errorData = await response.json
      const errorMessage = errorData.errors?.join(', ') || defaultMessage
      showFlashMessage(errorMessage)
    } catch {
      showFlashMessage(defaultMessage)
    }
  }

  isValidDrop(evt) {
    const targetSlot = evt.to
    return !(targetSlot === evt.from || targetSlot.getAttribute('data-calendar--sortable-target') !== 'slot')
  }

  extractEventData(evt) {
    return {
      item: evt.item,
      position: evt.newIndex,
      targetSlot: evt.to
    }
  }

  async submitAddForm(form, previousState, defaultErrorMessage, onSuccess) {
    try {
      const response = await post(form.action, {
        body: new FormData(form)
      })

      if (response.ok) {
        const data = await response.json
        if (data.html) {
          const { item } = previousState
          item.outerHTML = data.html
        }

        if (data.turbo_streams) {
          renderStreamMessage(data.turbo_streams)
        }

        if (onSuccess) {
          onSuccess()
        }
      } else {
        await this.handleErrorResponse(response, defaultErrorMessage)
        this.rollbackAdd(previousState)
      }
    } catch (error) {
      showFlashMessage('Network error. Please try again.')
      this.rollbackAdd(previousState)
    }
  }

}
